/** THE MANUAL — the steps of ONE function, and the way to send them. */
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Check, Copy, Link2, Mail, MessageSquare, Send } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import ManualSteps from '../../components/manual/ManualSteps';

const btn = "rounded-xl border border-slate-300 dark:border-slate-600 px-4 py-2.5 text-sm font-semibold hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors disabled:opacity-50";

const ManualSection = () => {
    const { sectionId } = useParams();
    const navigate = useNavigate();
    const [row, setRow] = useState(null);
    const [link, setLink] = useState(null);
    const [open, setOpen] = useState(false);
    const [phone, setPhone] = useState('');
    const [email, setEmail] = useState('');
    const [busy, setBusy] = useState('');

    const load = () => api.get(`/manual/sections/${sectionId}`).then((res) => {
        setRow(res.data.section);
        setLink(res.data.link?.url || null);
    });
    useEffect(() => { load(); }, [sectionId]);  // eslint-disable-line

    const makeLink = async () => {
        setBusy('link');
        try {
            const res = await api.post(`/manual/sections/${sectionId}/link`);
            setLink(res.data.url);
            await navigator.clipboard?.writeText(res.data.url).catch(() => {});
            toast.success('Link ready and copied');
        } finally { setBusy(''); }
    };

    const killLink = async () => {
        setBusy('link');
        try {
            await api.post(`/manual/sections/${sectionId}/link/off`);
            setLink(null);
            toast.success('Link turned off');
        } finally { setBusy(''); }
    };

    const send = async (channel) => {
        const to = channel === 'sms' ? phone.trim() : email.trim();
        if (!to) return;
        setBusy(channel);
        try {
            const res = await api.post(`/manual/sections/${sectionId}/send`,
                                       { channel, to });
            setLink(res.data.url);
            if (res.data.sent) toast.success(channel === 'sms' ? 'Text sent' : 'Email sent');
            else toast.error(res.data.error || 'It did not go out');
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'It did not go out');
        } finally { setBusy(''); }
    };

    if (!row) return <div className="p-8 text-sm text-slate-400">Loading…</div>;

    return (
        <div className="max-w-3xl mx-auto px-4 py-8" data-testid={`manual-section-${row.id}`}>
            <button type="button" onClick={() => navigate('/manual')}
                    data-testid="manual-back"
                    className="inline-flex items-center gap-1.5 text-sm text-slate-500 mb-5 hover:text-emerald-500 transition-colors">
                <ArrowLeft className="w-4 h-4" aria-hidden="true" /> Manual
            </button>

            <ManualSteps section={row} />

            <div className="mt-10 border-t border-slate-200 dark:border-slate-700 pt-6">
                <button type="button" onClick={() => setOpen((v) => !v)}
                        data-testid="manual-send-open"
                        className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white px-5 py-3 text-base font-semibold transition-colors">
                    <Send className="w-4 h-4" aria-hidden="true" /> Send this section
                </button>

                {open && (
                    <div className="mt-5 space-y-5" data-testid="manual-send-panel">
                        <div className="flex flex-wrap items-center gap-2">
                            <MessageSquare className="w-4 h-4 text-slate-400" aria-hidden="true" />
                            <input value={phone} onChange={(e) => setPhone(e.target.value)}
                                   placeholder="+1514…" data-testid="manual-send-phone"
                                   className="rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2.5 text-sm w-48" />
                            <button type="button" className={btn} disabled={busy === 'sms'}
                                    onClick={() => send('sms')} data-testid="manual-send-sms">
                                Text it
                            </button>
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                            <Mail className="w-4 h-4 text-slate-400" aria-hidden="true" />
                            <input value={email} onChange={(e) => setEmail(e.target.value)}
                                   placeholder="name@example.com" data-testid="manual-send-email"
                                   className="rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2.5 text-sm w-64" />
                            <button type="button" className={btn} disabled={busy === 'email'}
                                    onClick={() => send('email')} data-testid="manual-send-email-btn">
                                Email it
                            </button>
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                            <Link2 className="w-4 h-4 text-slate-400" aria-hidden="true" />
                            {link ? (
                                <>
                                    <span className="text-xs text-slate-500 break-all max-w-md"
                                          data-testid="manual-link-url">{link}</span>
                                    <button type="button" className={btn}
                                            onClick={() => { navigator.clipboard?.writeText(link); toast.success('Copied'); }}
                                            data-testid="manual-link-copy">
                                        <Copy className="w-4 h-4 inline mr-1.5" aria-hidden="true" />Copy link
                                    </button>
                                    <button type="button" className={btn} disabled={busy === 'link'}
                                            onClick={killLink} data-testid="manual-link-off">
                                        Turn this link off
                                    </button>
                                </>
                            ) : (
                                <button type="button" className={btn} disabled={busy === 'link'}
                                        onClick={makeLink} data-testid="manual-link-make">
                                    <Check className="w-4 h-4 inline mr-1.5" aria-hidden="true" />Make a link
                                </button>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ManualSection;
